import { action, observable, makeObservable } from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';

// CSSStyleDeclaration
type CssStyles = { [index: string]: string };

export class ReactFlipperStore {
  @observable
  isFlipped: boolean;

  constructor() {
    makeObservable(this);
    this.isFlipped = false;
  }

  @action
  setIsFlipped = (isFlipped: boolean) => {
    if (this.isFlipped !== isFlipped)
      this.isFlipped = isFlipped;
  };

  @action
  toggle = () => {
    this.isFlipped = !this.isFlipped;
  };
}

export interface ReactFlipperProps {
  store: ReactFlipperStore;

  styles?: {
    container?: CssStyles,
    front?: CssStyles;
    back?: CssStyles
  };

  children: [React.ReactNode, React.ReactNode];
}

function rotateY(degrees: number): string {
  return `rotateY(${degrees}deg)`;
}

@observer
export class ReactFlipper extends React.Component<ReactFlipperProps> {

  getChildren(): [React.ReactNode, React.ReactNode] {
    if (this.props.children.length !== 2) {
      throw new Error(
        'Component ReactFlipper requires 2 children to function',
      );
    }
    return this.props.children;
  }

  render() {
    const propsStyles = this.props.styles || {};
    const store = this.props.store;
    const isFlipped = store.isFlipped;

    const [front, back] = this.getChildren();

    const frontRotateY = isFlipped ? -180 : 0;
    const backRotateY = isFlipped ? 0 : 180;

    const styles: any = {
      back: {
        backfaceVisibility: 'hidden',
        height: '100%',
        left: '0',
        position: isFlipped ? 'relative' : 'absolute',
        top: '0',
        transform: rotateY(backRotateY),
        transformStyle: 'preserve-3d',
        transition: '0.5s',
        width: '100%',
        ...propsStyles.back,
      },
      container: {
        perspective: '1000px',
        zIndex: 'auto',
      },
      flipper: {
        height: '100%',
        position: 'relative',
        perspective: '1000px',
        width: '100%',
      },
      front: {
        backfaceVisibility: 'hidden',
        height: '100%',
        left: '0',
        position: isFlipped ? 'absolute' : 'relative',
        top: '0',
        transform: rotateY(frontRotateY),
        transformStyle: 'preserve-3d',
        transition: '0.5s',
        width: '100%',
        zIndex: '2',
        ...propsStyles.front,
      },
    };

    return (
      <div className="react-flipper-container"
           style={{...styles.container, ...propsStyles.container}}>
        <div className="react-flipper" style={styles.flipper}>
          <div className="react-flipper-front" style={styles.front}>
            {front}
          </div>

          <div className="react-flipper-back" style={styles.back}>
            {back}
          </div>
        </div>
      </div>
    );
  }
}
